/**
 * MarkdownContent
 * Renders AI chat message text as formatted Markdown.
 *
 * Uses react-markdown (v6) + remark-gfm for proper GFM support
 * (tables, strikethrough, task lists, autolinks).
 * Output is wrapped in Cloudscape TextContent for correct typography.
 *
 * Security: react-markdown renders to React elements (no dangerouslySetInnerHTML).
 * A model reply is untrusted, so link and image URLs are allow-list checked
 * (see markdownSafety):
 *   - an image is loaded only from an allow-listed / same-origin host; a
 *     disallowed image is rendered as its alt text and never requested, so a
 *     model reply cannot beacon an attacker host on render;
 *   - an allow-listed link opens in a new tab with rel="noopener noreferrer";
 *     a disallowed link is rendered as inert plain text, not a live anchor.
 */

import React, { useMemo } from 'react';
import { TextContent } from '@amzn/awsui-components-react';
import ReactMarkdown from 'react-markdown';
import { resolveAllowedUrl } from './markdownSafety';
import './MarkdownContent.scss';

// remark-gfm adds GitHub Flavored Markdown: tables, strikethrough, task lists, autolinks.
// Loaded lazily so the component still works if the package isn't installed yet.
let remarkGfm: any;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  remarkGfm = require('remark-gfm');
  // remark-gfm v1 exports the plugin as default or as the module itself
  if (remarkGfm && remarkGfm.default) remarkGfm = remarkGfm.default;
} catch {
  remarkGfm = null;
}

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Custom renderers — allow-list link and image URLs. Exported so every
 * react-markdown render of model text (e.g. ThinkingBubble) uses the same gate.
 *
 * `a`: a link to an allow-listed / same-origin host becomes an anchor that
 * opens in a new tab with rel="noopener noreferrer"; any other link is
 * rendered as its (inert) child text, so it is not clickable.
 *
 * `img`: an image from an allow-listed / same-origin host is rendered; any
 * other image is replaced by its alt text and never requested by the browser.
 */
export const safeMarkdownComponents = {
  // `node` is react-markdown's AST node; never spread it onto the DOM element.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  a: ({ href, children, node, ...props }: any) => {
    const safeHref = resolveAllowedUrl(href);
    if (!safeHref) {
      // Inert: render the link text only, never a live anchor.
      return <>{children}</>;
    }
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    );
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  img: ({ src, alt, node, ...props }: any) => {
    const safeSrc = resolveAllowedUrl(src);
    if (!safeSrc) {
      // Do not load a disallowed image — show its alt text instead so no
      // request is made to an attacker-controlled host.
      return <>{alt || ''}</>;
    }
    return <img src={safeSrc} alt={alt || ''} {...props} />;
  },
};

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className }) => {
  const plugins = useMemo(() => (remarkGfm ? [remarkGfm] : []), []);

  return (
    <TextContent>
      <div className={`genai-markdown-content${className ? ` ${className}` : ''}`}>
        <ReactMarkdown
          remarkPlugins={plugins}
          components={safeMarkdownComponents}
          // Disallow raw HTML from the agent — render it as escaped text instead
          skipHtml={false}
          allowedElements={[
            'p', 'br', 'strong', 'em', 'del', 'code', 'pre',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', 'input',
            'blockquote', 'hr', 'a', 'img',
            'table', 'thead', 'tbody', 'tr', 'th', 'td',
          ]}
        >
          {content || ''}
        </ReactMarkdown>
      </div>
    </TextContent>
  );
};
